/**
 * **The answer page** (James, Oct 2: the invitation is a question).
 *
 * The invitation asks "Will you be back?" with three buttons; each lands here with its answer
 * chosen and a Confirm. The link is the credential — one per invitation, good until the year's
 * invitations close, and good as often as it is opened — so nothing here asks anybody to sign
 * in, and nothing here says anything about the person but their first name and their seat.
 *
 * - **Yes** → "Thank you, James. Your 2027 packet comes today." One line, done.
 * - **Last year** → the same thank-you, after an optional "Would you help find your
 *   successor?" and a note for the directors.
 * - **No** → "Thanks for telling us.", after an optional reason (the directors read it; nobody
 *   who follows them does) and "May we ask you again?" — yes unless they say otherwise.
 *
 * Opening the link again shows the answer given and lets it be changed until the close; each
 * change is a new line in Who did what, never an overwrite.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { api, sentence } from '../../shell/api.js';
import { LoginFrame } from './Login.js';

type Answer = 'yes' | 'last_year' | 'no';

interface View {
  festivalYear: number;
  firstName: string | null;
  role: string;
  roleName: string;
  areaName: string | null;
  heldBefore: boolean;
  answer: Answer | null;
  answeredAt: string | null;
  closesOn: string;
  open: boolean;
  packetOn: string | null;
  packetSentAt: string | null;
  successorHelp: boolean | null;
  successorNote: string | null;
  askAgain: boolean | null;
  reason: string | null;
}

/** The address's last word, as the mail's buttons write it. */
const FROM_ADDRESS: Record<string, Answer> = { yes: 'yes', last: 'last_year', no: 'no' };

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
/** `2027-03-05` → "Friday, March 5": a calendar day, read as written — never moved by a zone. */
function dayWords(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const at = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  return `${DAYS[at.getUTCDay()] ?? ''}, ${MONTHS[(m ?? 1) - 1] ?? ''} ${String(d ?? 1)}`;
}

function wordsFor(a: Answer, v: View): string {
  const y = String(v.festivalYear);
  if (a === 'yes') return v.heldBefore ? `Yes, I’m back for ${y}` : `Yes, I’m in for ${y}`;
  if (a === 'last_year') return `Yes — and ${y} will be my last year`;
  return 'No, not this year';
}

function seatSays(v: View): string {
  const area = v.areaName ? `the ${v.areaName.replace(/^the\s+/i, '')}` : null;
  const last = String(v.festivalYear - 1);
  if (v.heldBefore)
    return v.role === 'area_chair' && area
      ? `You chaired ${area} in ${last}.`
      : `You were ${v.roleName} in ${last}.`;
  return v.role === 'area_chair' && area
    ? `We would like you to chair ${area} in ${String(v.festivalYear)}.`
    : `We would like you to be ${v.roleName} in ${String(v.festivalYear)}.`;
}

export function AnswerPage({ token, choice }: { token: string; choice: string | null }): ReactNode {
  const [view, setView] = useState<View | null>(null);
  const [dead, setDead] = useState<string | null>(null);
  const [picked, setPicked] = useState<Answer | null>(
    choice ? (FROM_ADDRESS[choice] ?? null) : null,
  );
  const [help, setHelp] = useState<boolean | null>(null);
  const [helpNote, setHelpNote] = useState('');
  const [reason, setReason] = useState('');
  const [askAgain, setAskAgain] = useState(true);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  /** Just confirmed: the thank-you stands in place of the form. */
  const [done, setDone] = useState(false);

  useEffect(() => {
    api<View>('GET', `/auth/answer/${token}`)
      .then((v) => {
        setView(v);
        setHelp(v.successorHelp);
        setHelpNote(v.successorNote ?? '');
        setReason(v.reason ?? '');
        setAskAgain(v.askAgain ?? true);
        // The address chose an answer only when the mail's button was pressed; opened again,
        // the page shows the answer given until somebody picks another.
        setPicked((p) => p ?? v.answer);
      })
      .catch((e: unknown) => {
        setDead(sentence(e));
      });
  }, [token]);

  const confirm = (): void => {
    if (!picked) {
      setRefusal('Choose an answer first.');
      return;
    }
    setBusy(true);
    setRefusal(null);
    api<View>('POST', `/auth/answer/${token}`, {
      answer: picked,
      ...(picked === 'last_year'
        ? { successorHelp: help, successorNote: helpNote.trim() ? helpNote.trim() : null }
        : {}),
      ...(picked === 'no' ? { reason: reason.trim() ? reason.trim() : null, askAgain } : {}),
    })
      .then((v) => {
        setView(v);
        setDone(true);
      })
      .catch((e: unknown) => {
        setRefusal(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (dead)
    return (
      <LoginFrame>
        <section className="login__card2" data-screen="answer">
          <div className="login__eyebrow">FROM THE FESTIVAL DIRECTORS</div>
          <h2>This link is not live</h2>
          <p role="alert">{dead}</p>
        </section>
      </LoginFrame>
    );
  if (!view) return <div className="boot">Opening your invitation…</div>;

  const y = String(view.festivalYear);
  const thanks =
    view.answer === 'no'
      ? 'Thanks for telling us.'
      : `Thank you${view.firstName ? `, ${view.firstName}` : ''}. Your ${y} packet comes ${
          view.packetOn ? `on ${dayWords(view.packetOn)}` : 'today'
        }.`;

  return (
    <LoginFrame>
      <section className="login__card2 login__card2--invite" data-screen="answer">
        <div className="login__eyebrow">FROM THE FESTIVAL DIRECTORS</div>
        <h2>{`Will you be ${view.heldBefore ? 'back' : 'with us'} for ${y}?`}</h2>
        <p>
          {view.firstName ? `${view.firstName}, ` : ''}
          {seatSays(view)}
        </p>
        {done && view.answer ? (
          <p role="status" data-testid="answer-thanks">
            {thanks}
          </p>
        ) : (
          <div className="login__card2-body">
            {view.answer && view.answeredAt ? (
              <p data-testid="answer-given">
                Your answer: <b>{wordsFor(view.answer, view)}</b>
                {view.open ? ' — you can change it below.' : '.'}
              </p>
            ) : null}
            {view.open ? (
              <>
                <div className="login__doors" role="radiogroup" aria-label="Your answer">
                  {(['yes', 'last_year', 'no'] as const).map((a) => (
                    <button
                      key={a}
                      type="button"
                      role="radio"
                      aria-checked={picked === a}
                      className={`login__door${picked === a ? ' login__door--on' : ''}`}
                      disabled={busy}
                      onClick={() => {
                        setPicked(a);
                        setRefusal(null);
                      }}
                    >
                      <span>{wordsFor(a, view)}</span>
                    </button>
                  ))}
                </div>
                {picked === 'last_year' ? (
                  <>
                    <p>Would you help find your successor? (optional)</p>
                    <div
                      className="login__doors"
                      role="radiogroup"
                      aria-label="Help find a successor"
                    >
                      {([true, false] as const).map((h) => (
                        <button
                          key={String(h)}
                          type="button"
                          role="radio"
                          aria-checked={help === h}
                          className={`login__door${help === h ? ' login__door--on' : ''}`}
                          onClick={() => {
                            setHelp(h);
                          }}
                        >
                          <span>{h ? 'Yes, I’ll help' : 'No, thank you'}</span>
                        </button>
                      ))}
                    </div>
                    <textarea
                      className="login__input"
                      aria-label="A note for the directors (optional)"
                      placeholder="A note for the directors (optional)"
                      maxLength={300}
                      value={helpNote}
                      onChange={(e) => {
                        setHelpNote(e.target.value);
                      }}
                    />
                  </>
                ) : null}
                {picked === 'no' ? (
                  <>
                    <input
                      className="login__input"
                      aria-label="Why, if you’d like to say (optional)"
                      placeholder="Why, if you’d like to say (optional)"
                      maxLength={200}
                      value={reason}
                      onChange={(e) => {
                        setReason(e.target.value);
                      }}
                    />
                    <p className="login__fine">
                      The directors read it. Nobody who follows you in the seat does.
                    </p>
                    <p>{`May we ask you again for ${String(view.festivalYear + 1)}?`}</p>
                    <div className="login__doors" role="radiogroup" aria-label="Ask again">
                      {([true, false] as const).map((a) => (
                        <button
                          key={String(a)}
                          type="button"
                          role="radio"
                          aria-checked={askAgain === a}
                          className={`login__door${askAgain === a ? ' login__door--on' : ''}`}
                          onClick={() => {
                            setAskAgain(a);
                          }}
                        >
                          <span>{a ? 'Yes, ask me again' : 'No, please don’t'}</span>
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
                {refusal ? (
                  <p className="login__refusal" role="alert">
                    {refusal}
                  </p>
                ) : null}
                <button className="login__go" type="button" disabled={busy} onClick={confirm}>
                  Confirm
                </button>
                <p className="login__fine">
                  You can change your answer any time before {dayWords(view.closesOn)}.
                </p>
              </>
            ) : (
              <p className="login__fine">
                Answers for {y} closed on {dayWords(view.closesOn)}. Write to the directors to
                change yours.
              </p>
            )}
          </div>
        )}
      </section>
    </LoginFrame>
  );
}
